import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Sparkles, MapPin, Plus, UserPlus, Rocket,
  ArrowRight, Check, X, Layers, Boxes, Activity, ChevronLeft,
} from 'lucide-react';
import '@fontsource/outfit/600.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OnboardingDashboard — first-time user 5-step tour.
 *
 * Triggered when user.onboarding_completed === false. Renders a full-screen
 * darkened overlay with a single panel that walks through Welcome → HUD tour
 * → Create project → Invite partner → CTA. Skipable at any step.
 *
 * Side effects on completion:
 *   - PUT /api/auth/onboarding {completed: true}
 *   - Optionally invokes onOpenWizard / onOpenInvite to chain into real flows.
 */
const STEPS = [
  {
    n: 1, key: 'welcome',
    icon: Sparkles,
    title: 'Bienvenue dans Shoebox3D',
    subtitle: 'Hub Cartographique · Peter\'s Universe',
    body: 'Vous êtes maintenant sur le cockpit cartographique qui pilote vos projets immobiliers en 3D. En 5 minutes, je vous fais visiter.',
  },
  {
    n: 2, key: 'tour',
    icon: Layers,
    title: 'Le HUD — votre cockpit',
    subtitle: 'Cartes flottantes · Drag & resize',
    body: 'Le tableau de bord est composé de widgets HUD que vous pouvez déplacer et redimensionner. Vos shoeboxes récentes, l\'activité de vos projets, l\'agenda — tout est sous les yeux, sans changer d\'écran.',
    pillars: [
      { icon: Boxes, label: 'Dernières Shoeboxes' },
      { icon: Activity, label: 'Activité récente' },
      { icon: MapPin, label: 'Carte interactive' },
    ],
  },
  {
    n: 3, key: 'create',
    icon: Plus,
    title: 'Créez votre 1er projet',
    subtitle: 'Wizard 4 étapes · Sans plans complets requis',
    body: 'Le wizard "Nouveau Projet" vous guide en 4 étapes : nom + localisation, type (appartement, résidence, etc.), détails contextuels, récap. Pas besoin d\'avoir tous les plans tout de suite — vous pourrez ajouter les documents plus tard.',
    cta: 'Créer mon 1er projet',
    ctaAction: 'wizard',
  },
  {
    n: 4, key: 'invite',
    icon: UserPlus,
    title: 'Invitez vos partenaires',
    subtitle: 'Communauté privée · Sur invitation',
    body: 'Shoebox3D fonctionne en réseau. Invitez vos clients, partenaires (architectes, agents) ou collègues pour partager vos catalogues, vos projets ou simplement collaborer. Chaque invitation est modérée par l\'équipe.',
    cta: 'Inviter un partenaire',
    ctaAction: 'invite',
  },
  {
    n: 5, key: 'ready',
    icon: Rocket,
    title: 'Prêt à décoller',
    subtitle: 'Votre cockpit est à vous',
    body: 'C\'est tout pour la visite. Vous pouvez relancer ce tour à tout moment depuis votre profil. À vous de jouer !',
    cta: 'Aller au Hub',
    ctaAction: 'finish',
  },
];

const OnboardingDashboard = ({ onOpenWizard, onOpenInvite }) => {
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(0);
  const [show, setShow] = useState(false);
  const [closing, setClosing] = useState(false);

  // Trigger only when user is loaded AND onboarding not completed
  // AND the personalization wizard (profile_setup_completed) is already done.
  // This ensures the wizard always runs FIRST.
  useEffect(() => {
    if (user && user.onboarding_completed === false && user.profile_setup_completed === true) {
      const t = setTimeout(() => setShow(true), 500);
      return () => clearTimeout(t);
    }
  }, [user]);

  const persistComplete = async () => {
    try {
      await axios.put(`${API}/auth/onboarding`, { completed: true }, { withCredentials: true });
      if (updateUser) updateUser({ ...user, onboarding_completed: true });
    } catch {
      // best-effort; tour will simply re-trigger next session
    }
  };

  const close = async (markComplete = true) => {
    setClosing(true);
    setTimeout(() => { setShow(false); setClosing(false); setStep(0); }, 280);
    if (markComplete) await persistComplete();
  };

  const handleCta = async (action) => {
    if (action === 'wizard') {
      await close(true);
      setTimeout(() => onOpenWizard && onOpenWizard(), 350);
    } else if (action === 'invite') {
      await close(true);
      setTimeout(() => onOpenInvite && onOpenInvite(), 350);
    } else if (action === 'finish') {
      await close(true);
    }
  };

  if (!show) return null;
  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div
      className={`fixed inset-0 z-[3000] flex items-center justify-center p-4 sm:p-6 backdrop-blur-md transition-opacity duration-300 ${
        closing ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ background: 'rgba(7,7,9,0.78)' }}
      data-testid="onboarding-overlay"
    >
      <style>{`
        @keyframes onbFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes onbGlow { 0%,100% { opacity: .55; } 50% { opacity: .85; } }
      `}</style>

      <div
        className="relative w-full max-w-xl bg-gradient-to-b from-[#101013] to-[#0a0a0c] border border-white/10 rounded-3xl shadow-2xl overflow-hidden"
        style={{ animation: 'onbFadeUp 380ms ease-out' }}
        data-testid="onboarding-panel"
      >
        {/* Halo glow */}
        <div
          className="pointer-events-none absolute -top-32 -left-32 w-80 h-80 rounded-full blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(217,160,91,0.35) 0%, rgba(217,160,91,0) 65%)',
            animation: 'onbGlow 4s ease-in-out infinite',
          }}
        />

        {/* Header */}
        <div className="relative px-6 sm:px-8 pt-6 pb-4 flex items-center justify-between border-b border-white/8">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#D9A05B] font-bold">
            <Sparkles className="w-3 h-3" />
            Tour guidé · {step + 1}/{STEPS.length}
          </div>
          <button
            onClick={() => close(true)}
            data-testid="onboarding-skip-btn"
            className="text-[10px] uppercase tracking-widest text-white/40 hover:text-white px-2.5 py-1 rounded-md hover:bg-white/5 inline-flex items-center gap-1.5"
          >
            Passer
            <X className="w-3 h-3" />
          </button>
        </div>

        {/* Body */}
        <div className="relative px-6 sm:px-10 py-8" data-testid={`onboarding-step-${s.key}`}>
          <div
            className="w-14 h-14 rounded-2xl bg-[#D9A05B]/15 border border-[#D9A05B]/30 flex items-center justify-center mb-5"
            style={{ boxShadow: '0 0 36px rgba(217,160,91,0.20)' }}
          >
            <Icon className="w-6 h-6 text-[#D9A05B]" />
          </div>

          <p className="text-[10px] uppercase tracking-[0.28em] text-white/40 font-bold mb-2">
            {s.subtitle}
          </p>
          <h2
            className="text-2xl sm:text-3xl font-semibold text-white leading-tight mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {s.title}
          </h2>
          <p className="text-sm sm:text-base text-white/60 leading-relaxed">
            {s.body}
          </p>

          {/* HUD pillars (step 2) */}
          {s.pillars && (
            <div className="grid grid-cols-3 gap-2 mt-6">
              {s.pillars.map((p, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/[0.03] border border-white/10"
                >
                  <p.icon className="w-4 h-4 text-[#D9A05B]" />
                  <span className="text-[10px] uppercase tracking-widest text-white/55 font-bold text-center leading-tight">
                    {p.label}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Step dots */}
        <div className="px-6 sm:px-10 pb-3 flex items-center justify-center gap-1.5">
          {STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              data-testid={`onboarding-dot-${i}`}
              aria-label={`Aller à l'étape ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? 'w-7 bg-[#D9A05B]' : i < step ? 'w-1.5 bg-[#D9A05B]/60' : 'w-1.5 bg-white/15 hover:bg-white/30'
              }`}
            />
          ))}
        </div>

        {/* Footer actions */}
        <div className="px-6 sm:px-10 pb-6 flex items-center justify-between gap-3">
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            data-testid="onboarding-back-btn"
            className="text-xs text-white/45 hover:text-white px-3 py-2 rounded-lg hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Précédent
          </button>

          <div className="flex items-center gap-2">
            {s.cta ? (
              <button
                onClick={() => handleCta(s.ctaAction)}
                data-testid="onboarding-cta-btn"
                className="bg-[#D9A05B] hover:bg-[#C78D46] text-black font-medium h-10 px-5 rounded-lg inline-flex items-center gap-1.5 shadow-[0_0_18px_rgba(217,160,91,0.35)]"
              >
                {s.ctaAction === 'finish' ? <Rocket className="w-3.5 h-3.5" /> : null}
                {s.cta}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                onClick={() => setStep(Math.min(STEPS.length - 1, step + 1))}
                data-testid="onboarding-next-btn"
                className="bg-[#D9A05B] hover:bg-[#C78D46] text-black font-medium h-10 px-5 rounded-lg inline-flex items-center gap-1.5"
              >
                Suivant
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingDashboard;
